/**
 * First-sync progress — polled, not driven.
 *
 * ## Why the browser no longer owns the walk
 *
 * The personal lane's entire ingest used to be `usePersonalInboxSync`: a React
 * mutation walking five pages of a hundred, in one component, in one tab. It
 * could not answer "how far through am I" because nothing had asked the
 * provider how much mail there was, and it could not survive a refresh, a
 * second tab, or the user typing `/inbox` straight into the address bar —
 * which is exactly the case the brief calls out.
 *
 * The walk now runs server-side against a job row
 * (`services/mailbox_sync_jobs.py`). This hook reads that row. Every tab sees
 * the same numbers, a refresh resumes the same bar, and closing the laptop does
 * not stop the sync.
 *
 * ## The poll interval is deliberately two speeds
 *
 * While something is running the modal is on screen and the numbers must move,
 * so 2s. Once everything is finished there is nothing to watch, and polling an
 * endpoint that will answer the same thing forever is pure cost — so the poll
 * stops entirely rather than settling into a slow heartbeat. A new job arrives
 * through an explicit `refetch()` from whatever started it.
 */
import React, { createContext, useContext, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../context/AuthContext';
import { apiFetch, apiPost } from '../lib/api';

export const syncProgressKeys = {
    lane: (inboxId) => ['mail', 'sync-progress', inboxId || 'personal'],
};

/** How often the bar updates while a walk is live. */
export const POLL_MS = 2000;

/**
 * A stable empty model, so a pending or failed query does not hand the modal a
 * new object identity every render.
 */
export const EMPTY_PROGRESS = Object.freeze({
    jobs: Object.freeze([]),
    blocking: false,
});

/**
 * How often to re-read the lane, given what it currently holds.
 *
 * `false` means STOP, not "slow down". Once nothing is live the endpoint will
 * answer the same thing forever, and a background heartbeat against it is pure
 * cost on every open inbox tab in the tenant — which, for the overwhelming
 * majority of users (no first-sync job has ever existed for them), is every
 * tab, always. A new job arrives through an explicit invalidation from
 * whatever started it.
 *
 * `ready` counts as live: the remainder is still walking behind the user and
 * the strip is showing its numbers.
 *
 * Exported as a pure function so the rule can be tested without running
 * timers, which is the only reliable way to assert that something stops.
 */
export function pollIntervalFor(jobs) {
    const live = (jobs || []).some(
        (job) => job.status === 'pending'
            || job.status === 'running'
            || job.status === 'ready'
    );
    return live ? POLL_MS : false;
}

/**
 * First-sync jobs for one lane.
 *
 * @param {string|null} inboxId  a unified inbox id, or null for the personal
 *                               lane (the parameter is simply omitted from the
 *                               request, which is what the endpoint reads as
 *                               "personal").
 * @param {object}  [options]
 * @param {boolean} [options.enabled]
 */
export function useSyncProgress(inboxId = null, { enabled = true } = {}) {
    const { getAuthHeader, user } = useAuth();

    const query = useQuery({
        queryKey: syncProgressKeys.lane(inboxId),
        enabled: Boolean(user) && enabled !== false,
        queryFn: () => {
            const qs = inboxId
                ? `?${new URLSearchParams({ inbox_id: inboxId }).toString()}`
                : '';
            return apiFetch(`/inbox/sync/progress${qs}`, getAuthHeader());
        },
        select: (response) => response?.data || EMPTY_PROGRESS,
        // No retry. The expected failures are 401 (session gone) and 403 (the
        // grant was revoked), and neither improves on a second attempt —
        // meanwhile the modal would sit there claiming to be syncing.
        retry: false,
        // `query.state.data` is the RAW envelope here, not the `select`ed
        // value — React Query runs `select` for observers, and this callback
        // reads the cache entry.
        refetchInterval: (query) =>
            pollIntervalFor(query.state.data?.data?.jobs),
        refetchOnWindowFocus: true,
    });

    const data = query.data || EMPTY_PROGRESS;
    return {
        // `EMPTY_PROGRESS.jobs`, not a fresh `[]`: a new array literal per
        // render would re-run every consumer's memo for nothing.
        jobs: data.jobs || EMPTY_PROGRESS.jobs,
        // The server decides this, not the client. The rule — only the WINDOW
        // phase holds the inbox — has to live in one place, or a client free to
        // reimplement it is free to disagree, and the disagreement either
        // strands someone behind a modal or drops them into an empty inbox.
        //
        // `false` while loading and on error, deliberately: a failed request
        // must never be able to lock a user out of their own inbox.
        blocking: query.isSuccess ? Boolean(data.blocking) : false,
        isLoading: query.isLoading,
        error: query.error,
        refetch: query.refetch,
    };
}

/**
 * Re-run a job that errored.
 *
 * Reached from the progress strip, not from the blocking dialog: a failed job
 * RELEASES the dialog, because holding someone out of the CRM until a provider
 * stops refusing pages turns one broken mailbox into a locked-out account.
 *
 * Invalidating brings the poll back to life — it stops itself once nothing is
 * running, so without this a retried job would move on the server and never on
 * screen.
 */
export function useRetrySync(inboxId = null) {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (jobId) =>
            apiPost(
                `/inbox/sync/${encodeURIComponent(jobId)}/retry`,
                {},
                getAuthHeader()
            ),
        onSuccess: () =>
            queryClient.invalidateQueries({ queryKey: syncProgressKeys.lane(inboxId) }),
    });
}

/**
 * Ask for a first sync of a provider that has none.
 *
 * This is the control for a mailbox connected BEFORE the job model existed:
 * those have no job row, which is what keeps this change from re-walking every
 * tenant's history at deploy time, and this is how such a user asks for the
 * backfill they never got.
 */
export function useStartSync(inboxId = null) {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (provider) =>
            apiPost(
                '/inbox/sync/start',
                { provider, ...(inboxId ? { inbox_id: inboxId } : {}) },
                getAuthHeader()
            ),
        onSuccess: () =>
            queryClient.invalidateQueries({ queryKey: syncProgressKeys.lane(inboxId) }),
    });
}

export default useSyncProgress;


// ─── One poller per lane ─────────────────────────────────────────────────────

/**
 * First-sync progress, shared by every consumer on a surface.
 *
 * ## Why a context and not just the hook
 *
 * Two components want this: `pages/Inbox.jsx` (the blocking dialog and the
 * strip) and `InboxFiltersBar` (the per-mailbox sliders). Both called
 * `useSyncProgress` with the same key — and React Query deduplicates the
 * *request in flight*, not the schedule: each observer gets its OWN
 * `setInterval`, so two out-of-phase 2s timers polled the lane roughly twice
 * per window for as long as a job was live.
 *
 * "Whichever component mounts first owns the poll" is not a rule anyone can
 * rely on, so ownership is made explicit instead: the page provides, everyone
 * else consumes.
 *
 * A consumer rendered outside a provider gets the empty model and a no-op
 * retry rather than throwing. `InboxFiltersBar` is used in places that have no
 * sync story at all, and a missing provider should cost them nothing.
 */
const SyncProgressContext = createContext(null);

export function SyncProgressProvider({ inboxId = null, children }) {
    const { jobs, blocking, isLoading, error, refetch } = useSyncProgress(inboxId);
    const retry = useRetrySync(inboxId);

    // Memoised on the values, not rebuilt per render: `jobs` is already stable
    // (see `EMPTY_PROGRESS`), so consumers' own memos stay valid between polls.
    const value = useMemo(
        () => ({ jobs, blocking, isLoading, error, refetch, retry, inboxId }),
        [jobs, blocking, isLoading, error, refetch, retry, inboxId],
    );

    return (
        <SyncProgressContext.Provider value={value}>
            {children}
        </SyncProgressContext.Provider>
    );
}

/** A stable fallback, so a provider-less consumer does not churn identities. */
const NO_PROGRESS = Object.freeze({
    jobs: EMPTY_PROGRESS.jobs,
    blocking: false,
    isLoading: false,
    error: null,
    refetch: () => {},
    retry: { mutate: () => {}, mutateAsync: async () => ({}) },
    inboxId: null,
});

export function useSyncProgressLane() {
    return useContext(SyncProgressContext) || NO_PROGRESS;
}

/**
 * The jobs that belong to individual mailboxes, keyed by address.
 *
 * Lives here rather than in the filter bar so the keying rule — lowercased,
 * trimmed, finished jobs dropped — has one definition. A mismatch between this
 * and the option rows' own `emailOf` is invisible: the slider simply never
 * appears.
 */
export function useSyncByMailbox() {
    const { jobs } = useSyncProgressLane();
    return useMemo(() => {
        const map = {};
        for (const job of jobs || []) {
            const key = String(job.mailbox || '').trim().toLowerCase();
            // A job with no mailbox is the whole-lane kind and has no row to
            // attach to. Finished ones are dropped so a completed import does
            // not leave a full bar sitting under every address.
            if (!key || job.status === 'complete') continue;
            map[key] = job;
        }
        return map;
    }, [jobs]);
}
